import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'
import { DAIMI_FRAMEWORK, DAIMI_HERO_PHOTO, DAIMI_SLUG } from '@/content/daimi'

/**
 * The Digital & AI Maturity Report 2026 — USA Edition page: its copy and its
 * figures, apart from the component that draws them.
 *
 * **Why this report has a page and twenty-eight others do not.** It is the third
 * designed report page after `content/daimi.ts` and `content/daimiConsumerGoods.ts`,
 * asked for on 2026-08-27 — *"daimi usa edition u bu webp e göre tasarla referans
 * al"* — with one attachment: a full-page screenshot of digitopia.co's USA Edition
 * page. Until then the USA Edition was drawn by the shared two-column template in
 * `pages/Reports.tsx` over the series description, and carried a
 * `stillToCome({ copy: true })` note saying exactly that.
 *
 * **The copy here was read off that screenshot, not supplied as text** — the same
 * position `content/daimi.ts` was in, and for the same reason its sections almost
 * all carry a `check`. A full-page capture of a 13,700px page renders body copy at
 * a handful of pixels, so the *shape* of the page is certain and the *wording and
 * the figures* are a careful transcription. On a benchmark report that distinction
 * matters more than anywhere else on this site: "2.99" and "2.89" are different
 * claims about the United States. `daimiUsaChecks()` feeds the open questions into
 * the same manifest as the rest of the site's amber notes, so a production build
 * cannot ship them unread.
 *
 * **Two figures on the live page are not here, and nothing stands in for them
 * dishonestly.** The band-two chart (Figure 1, the US DAIMI/DIMI/AIMI trend) and
 * the band-three chart (Figure 2, seventeen US industries) were not supplied as
 * files, and the only copies we hold — `daimi-maturity-index-gap.webp` and
 * `daimi-industry-benchmark.png` — are the **global** benchmark, not the US cut.
 * Putting a global chart under a caption that says "The US DAIMI Benchmark 2026"
 * would be a false claim rather than a placeholder. So each band shows the figures
 * it states in words, set as type in the site's own dark navy — the same move
 * `Cover` in `pages/Reports.tsx` makes for a report whose artwork has not arrived —
 * and asks for the export in amber.
 *
 * The three images the page does use are all files we already hold: the globe from
 * the DAIMI landing hero, the DAIMI wheel, and three photographs that match the
 * three playbook scenes on the live page.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const DAIMI_USA_SLUG = 'digital-ai-maturity-report-usa-edition'

/**
 * The hero headline, split the way the live page sets it: the report's name in
 * the foreground colour, "USA Edition" under it in the accent.
 *
 * Two fields rather than one string with markup in it, because the break is a
 * design decision about this page and not punctuation inside a sentence.
 */
export const DAIMI_USA_TITLE = {
  lead: 'Digital & AI Maturity Report',
  accent: 'USA Edition',
}

export const DAIMI_USA_INTRO = {
  body: [
    'The US is a global leader in digital transformation, but the frontier is moving. Discover the benchmarks, industry gaps, and the playbook for turning maturity into measurable business outcomes.',
  ],
  /**
   * The globe, in a dark card on the right of the hero — which is where the live
   * page puts the same kind of artwork.
   *
   * `DAIMI_HERO_PHOTO` reused rather than attached again: it is the file the
   * flagship report's hero already runs, a 1898px banner of dark navy with the
   * wired globe on its right-hand third. Cropped to a card it shows the globe,
   * which is the half that carries the picture. Decorative, so `alt` stays empty
   * — the headline beside it says everything it says.
   */
  photo: DAIMI_HERO_PHOTO,
  check:
    'Every word on this page is transcribed from the single full-page screenshot supplied on 2026-08-27, not from supplied text, so it needs reading against digitopia.co once before production — starting here, because `content/reports.ts` also takes this page’s search-result description from this paragraph.',
}

/**
 * The second band: three uncomfortable truths, and the US index scores beside
 * them.
 *
 * The three bullets are one block on the live page in which each line opens with
 * a bolded label. Split into `label` and `body` here for the same reason the
 * Consumer Goods page splits its three: "Technology Leveled Up. Maturity Didn’t:"
 * is a heading for what follows it, and running it into the paragraph is what
 * makes the three read as one grey slab.
 */
export const DAIMI_USA_CHASM = {
  heading: 'A Widening Chasm',
  lede: 'The report captures a critical turning point for US enterprises and reveals three uncomfortable truths that every leader must address to remain competitive.',
  points: [
    {
      label: 'Technology Leveled Up. Maturity Didn’t',
      body: 'With a DAIMI of 2.99, US maturity is nearly flat against the speed of innovation; while digital foundations (DIMI 3.12) are solid, AI maturity (AIMI 2.43) is still struggling to catch up.',
    },
    {
      label: 'FOMO is Noise. Value is the Signal',
      body: 'Despite “AI-first” hype, most industries fail to reshape end-to-end workflows. Real value lies in moving business metrics, not the “expensive noise” of scattered pilots.',
    },
    {
      label: 'Alarm Bells are Louder. The Gap is a Chasm',
      body: 'A massive score spread marks a structural divide. As the frontier moves toward autonomous value chains, catching up is now exponential, not linear.',
    },
  ],
  /**
   * The three index scores the missing Figure 1 plots, set as type.
   *
   * These are the values the first bullet states in the sentence above, so the
   * tiles restate the page rather than adding a claim to it — which is the whole
   * condition under which a stand-in for a chart is honest.
   */
  scores: [
    { value: '2.99', label: 'DAIMI', note: 'Digital & AI maturity' },
    { value: '3.12', label: 'DIMI', note: 'Digital maturity' },
    { value: '2.43', label: 'AIMI', note: 'AI maturity' },
  ],
  figureLabel: 'The US benchmark, 2026',
  caption: 'Figure 1: The US DAIMI Benchmark 2026: DIMI, AIMI and DAIMI',
  check:
    'Two things about this band. The chart itself — Figure 1 on digitopia.co, the US DIMI/AIMI/DAIMI trend — was not supplied as a file, and the two benchmark charts we do hold are the global cut rather than the US one, so the three scores are set as type instead of shown under a caption they would not match; send the export and it replaces them. And the third bullet ends “a massive score spread” where the live page prints the range: the band below reads it as 1.94–4.10, but it was too small to be certain of in the capture, so it is left out here rather than guessed at in the one place a reader would quote it.',
}

/**
 * The third band: the seventeen-industry benchmark.
 *
 * Same shape as the live page — a centred heading, a bold standfirst, **two more
 * centred lines under it**, then the wide chart with a figure caption under that,
 * the whole band sitting on a light panel rather than on the white page. The
 * chart is the one thing missing, so the three numbers the standfirst states are
 * the figure here, and `check` asks for the export.
 *
 * The frontrunners and laggards lines are a *pair* on the live page — two short
 * centred statements between the standfirst and the chart, each opening with a
 * bolded label. They were one line here, set inside the figure card, until the
 * two half-page captures of 2026-08-27 showed both of them and showed where they
 * sit. Split into `label`/`body` for the same reason the chasm bullets are:
 * "The Laggards — The Industrialization Gap:" is a heading for what follows it.
 */
export const DAIMI_USA_INDUSTRIES = {
  heading: 'The US Industry Benchmarks',
  lede: 'The 2026 US benchmark reveals a stark divide between sectors industrializing AI and those stalled at the foundation. While the national average is 2.99, the massive spread (1.94–4.10) shows leaders are pulling away through disciplined execution.',
  spread: [
    { value: '1.94', label: 'Lowest industry score' },
    { value: '2.99', label: 'US national average' },
    { value: '4.10', label: 'Highest industry score' },
    { value: '17', label: 'Industries benchmarked' },
  ],
  readings: [
    {
      label: 'The Frontrunners: Maturity as a KPI',
      body: 'Top industries treat maturity as a core metric for success.',
    },
    {
      label: 'The Laggards: The Industrialization Gap',
      body: 'Sectors most at risk of structural disadvantage suffer from fragmented strategy and “pilot overload”.',
    },
  ],
  caption: 'Figure 2: The US DAIMI Industry Benchmark across 17 Industries',
  check:
    'The seventeen-industry chart on the live page (Figure 2) was not supplied as a file. The industry names and their scores were not legible in the screenshots either, so nothing here names a sector or ranks one — the band states the range and the two readings under the standfirst, and stops. Send the export and this becomes the chart it is on digitopia.co. The frontrunners line is the least certain sentence on this page: it is one line of small type in a full-page capture, so read “Top industries treat maturity as a core metric for success” against digitopia.co before production.',
}

/**
 * The methodology band — dark, centred, the wheel under it.
 *
 * The wheel is `DAIMI_FRAMEWORK.photo`, the same file the DAIMI landing page and
 * the Consumer Goods page use, and the same drawing digitopia.co puts in this
 * band. The live band runs a navy-to-magenta gradient behind it; this one is the
 * site's flat `--ink`, the call `pages/Daimi.tsx` already made about the same
 * purple — one accent colour, and `src/index.css` says so at the top.
 */
export const DAIMI_USA_STANDARD = {
  heading: 'A New Standard for the Age of AI',
  body: 'The Digital and AI Maturity Index (DAIMI) is the evolution of Digitopia’s proven framework, designed to measure how effectively organizations translate technology into consistent business value. It evaluates maturity across six dimensions (Customer, Operations, Innovation, People, Governance, and Technology), and in doing so it measures both business outcomes and the capabilities required to deliver them consistently.',
  photo: DAIMI_FRAMEWORK.photo,
  learnHref: LINKS.daimi,
  learnLabel: 'Learn more',
}

/**
 * The playbook: three steps, each with a photograph beside it.
 *
 * **The live page shows a photograph against the first two steps and nothing
 * against the third**, which leaves a white half-row at the bottom of an
 * otherwise alternating band — it reads as a missing image rather than as a
 * decision. The third step gets one here, so the alternation completes; the
 * photographs are three we already hold and each matches the scene the live page
 * uses at that step. Say the word and the third comes out again.
 */
export const DAIMI_USA_PLAYBOOK = {
  heading: 'The DAIMI Playbook: From Activity to Impact',
  lede: 'In 2026, the greatest risk is doing a lot of AI work while achieving very little. While pilots multiply, true maturity often remains stagnant. The organizations pulling ahead',
  ledeStrong: 'measure reality, choose priorities, and execute relentlessly.',
  steps: [
    {
      heading: 'Assess Your Maturity: Take Control of the Baseline',
      body: 'You cannot manage what you do not measure. A DAIMI assessment replaces opinion with evidence, identifying exactly where you are strong and where you are exposed.',
      photo: {
        src: '/photo/open-office-team.jpg',
        alt: 'Digitopia colleagues working together in an open-plan office, a plan sketched on the whiteboard behind them.',
      } satisfies Photo,
    },
    {
      heading: 'Define Your Strategy: Make Tech Serve the Business',
      body: 'Strategy is a set of choices, not a wish list. Start with your business “why” (growth, cost, or resilience) and translate those into the specific capabilities you must build.',
      photo: {
        src: '/photo/daimi-score-presentation.jpg',
        alt: 'A Digitopia consultant presenting a DAIMI score against a target, dimension by dimension, to two executives.',
      } satisfies Photo,
    },
    {
      heading: 'Execute Your Roadmap: Turn Priorities into Value',
      body: 'Winners focus on a small number of high-value initiatives and scale them across the entire organization. They don’t confuse speed with progress; a pilot is only successful if it reaches production and changes outcomes.',
      photo: {
        src: '/photo/factory-roadmap.jpg',
        alt: 'A Digitopia consultant presenting a transformation roadmap on a quarterly timeline to a team on a factory floor.',
      } satisfies Photo,
    },
  ],
  expertLabel: 'Talk with an expert',
  expertHref: LINKS.contact,
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * **Two columns, not a centred block.** On digitopia.co this band is a card of
 * form fields on the left with the report standing in 3D on the right, and that
 * pairing is the whole shape of it — the second half-page capture of 2026-08-27
 * shows it plainly. The form itself cannot be reproduced (see `check`), so the
 * left card holds what the reader gets and the button that gets it; the right
 * column is the report's own cover, which is the file we hold of the artwork the
 * mockup is made from.
 *
 * `includes` states nothing the paragraph above it does not already state — it
 * is the same sentence set as a list, which is the only condition under which
 * standing something in for the form is honest rather than invented.
 */
export const DAIMI_USA_DOWNLOAD = {
  heading: 'Download Your Guide to Winning the Digital and AI Transformation',
  body: 'Gain exclusive access to the full 2026 US benchmark data, including in-depth industry rankings and the DAIMI methodology for scaling AI value.',
  cardLabel: 'What is in the report',
  includes: [
    'The full 2026 US benchmark data',
    'In-depth industry rankings',
    'The DAIMI methodology for scaling AI value',
  ],
  globalLabel: 'Explore the global report',
  globalHref: `/en/impact-labs/reports/${DAIMI_SLUG}`,
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiChecks()` and `daimiConsumerGoodsChecks()`
 * use, and appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'` on all four. Two of them do ask for a file,
 * but what they are really about is a claim: a chart captioned as the US
 * benchmark has to *be* the US benchmark, and that is a question for whoever
 * holds the data rather than a hole in the build.
 */
export function daimiUsaChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'intro', check: DAIMI_USA_INTRO.check },
    { section: 'chasm', check: DAIMI_USA_CHASM.check },
    { section: 'industries', check: DAIMI_USA_INDUSTRIES.check },
    { section: 'download', check: DAIMI_USA_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_USA_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
